import { throwError, of, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { WsUrlsService } from './wsurls.service';
import { LoginRequest, ForgotRequest, ResetRequest, RegisterRequest, ConfirmRequest, SendConfirmRequest } from '../_models/models';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class LoginService {
  public loginRequest: LoginRequest = {};

  private newTokenObservableTimer = 0;
  private newTokenObservable?: Observable<number> = null;

  constructor(
    private _http: HttpClient,
    private _storage: StorageService,
    private _urls: WsUrlsService
  ) { }

  doLogin(username: string, password: string): Observable<boolean> {

    this.loginRequest.username = username;
    this.loginRequest.password = password;
    this.loginRequest.token = null;

    const headers = {
      'Content-Type': 'application/json'
    };

    return this._http.post(this._urls.postLogin(), this.loginRequest, { headers: headers }).pipe(
      catchError(error => {
        if (error.status === 401) {
          return of(false);
        } else if (error.status === 400) {
          this._storage.saveEmailNotConfirmed();
          return of(false);
        } else {
          return throwError(error.status || 0);
        }
      }),
      map((res: any) => {
        if (res) {
          const data = res;
          this._storage.saveJWTaccessToken(data.access_token);
          this._storage.saveJWTrefreshToken(data.refresh_token);
          this._storage.saveUser(data.user);
          this._storage.saveCompanyId(data.companyId);
          this._storage.saveRoles(data.roles);
          return true;
        } else {
          return false;
        }
      })
    );
  }

  doForgot(email: string): Observable<any> {
    const forgotRequest: ForgotRequest = {};
    forgotRequest.email = email;

    const headers = {
      'Content-Type': 'application/json'
    };

    return this._http.post(this._urls.postForgot(), forgotRequest, { headers: headers }).pipe(
      catchError(error => {
        if (error.status === 401) {
          return of(false);
        } else {
          return throwError(error.status || 0);
        }
      })
    );
  }

  doReset(userId: string, password: string, confirmPassword: string, token: string): Observable<any> {

    const resetRequest: ResetRequest = {};
    resetRequest.userId = userId;
    resetRequest.password = password;
    resetRequest.confirmPassword = confirmPassword;
    resetRequest.token = token;

    const headers = {
      'Content-Type': 'application/json'
    };

    return this._http.post(this._urls.postReset(), resetRequest, { headers: headers }).pipe(
      catchError(error => {
        if (error.status === 401 || error.status === 400) {
          return of(false);
        } else {
          return throwError(error.status || 0);
        }
      }),
      map((res: any) => {
        return true;
      })
    );
  }

  doRegister(email: string, password: string, confirmPassword: string): Observable<any> {

    const registerRequest: RegisterRequest = {};
    registerRequest.email = email;
    registerRequest.password = password;
    registerRequest.confirmPassword = confirmPassword;

    const headers = {
      'Content-Type': 'application/json'
    };

    return this._http.post(this._urls.postRegister(), registerRequest, { headers: headers }).pipe(
      catchError(error => {
        if (error.status === 401) {
          return of(false);
        } else {
          return throwError(error.status || 0);
        }
      })
    );
  }

  doConfirm(userId: string, token: string): Observable<any> {

    const confirmRequest: ConfirmRequest = {};
    confirmRequest.userId = userId;
    confirmRequest.token = token;

    const headers = {
      'Content-Type': 'application/json'
    };

    return this._http.post(this._urls.postConfirm(), confirmRequest, { headers: headers }).pipe(
      catchError(error => {
        if (error.status === 401) {
          return of(false);
        } else {
          return throwError(error.status || 0);
        }
      })
    );
  }

  doGetNewTokenPair(): Observable<number> {
    // recycling observable only for calls within 5 seconds
    if ((new Date()).getTime() - this.newTokenObservableTimer > 5000) {
      this.newTokenObservable = null;
    }

    if (null != this.newTokenObservable) {
      // recycling observable
      return this.newTokenObservable;
    } else {
      this.newTokenObservableTimer = (new Date()).getTime();
    }

    this.loginRequest.token = this._storage.getJWTrefreshToken();

    const headers = {
      'Content-Type': 'application/json'
    };

    const csbj = new Subject<any>();

    this._http.post(this._urls.postLogin(), this.loginRequest, { headers: headers }).pipe(
      catchError(error => {
        // csbj.next( error.status );

        csbj.next(error.status);
        return of(error.status);
      })
    ).subscribe((res: any) => {
      if (res) {
        const data = res;

        this._storage.saveJWTaccessToken(data.access_token);
        this._storage.saveJWTrefreshToken(data.refresh_token);
        this._storage.saveCompanyId(data.companyId);
        this._storage.saveRoles(data.roles);

        csbj.next(200);
      }
    });

    this.newTokenObservable = csbj.asObservable();

    return this.newTokenObservable;
  }

  isLogged(): boolean {
    if (this._storage.getJWTaccesToken() != null) {
      return true;
    }

    return false;
  }

  doLogout() {
    this._storage.clearJWTrefreshToken();
    this._storage.clearJWTaccessToken();
    this._storage.clearUser();
    this._storage.clearCompanyId();
    this._storage.clearRoles();
  }

  doSendConfirm(email: string) {
    const sendConfirmRequest: SendConfirmRequest = {};
    sendConfirmRequest.email = email;

    const headers = {
      'Content-Type': 'application/json'
    };

    return this._http.post(this._urls.sendConfirm(), sendConfirmRequest, { headers: headers }).pipe(
      catchError(error => {
        if (error.status === 401) {
          return of(false);
        } else {
          return throwError(error.status || 0);
        }
      })
    );
  }
}
